import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calculator, MapPin, Truck, Phone, ShoppingBag } from "lucide-react";
import { Reveal } from "./Reveal";
import { Input } from "../components/ui/input";
import { BRAND } from "../data/content";
import { useCart } from "../context/CartContext";
import { classifyZip } from "../lib/deliveryZones";

export const DeliveryCalculator = () => {
  const [zip, setZip] = useState("");
  const [touched, setTouched] = useState(false);
  const { setDeliveryFee, setFulfillment, zip: cartZip } = useCart();

  const result = useMemo(() => classifyZip(zip), [zip]);
  const fiveDigits = /^\d{5}$/.test(zip.trim());
  const showInvalid = touched && zip.length > 0 && !fiveDigits;

  // Hydrate from cart on mount if a previous zip exists
  useEffect(() => {
    if (cartZip && !zip) setZip(cartZip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync any classified fee into the cart
  useEffect(() => {
    if (fiveDigits && result && result.fee != null) {
      setDeliveryFee(result.fee, zip);
      setFulfillment("delivery");
    }
  }, [zip, result, fiveDigits, setDeliveryFee, setFulfillment]);

  const onChange = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 5);
    setZip(v);
    if (!touched) setTouched(true);
  };

  return (
    <section
      id="delivery"
      data-testid="delivery-calculator-section"
      className="py-10 sm:py-14 lg:py-16 bg-brand-charcoal grain overflow-hidden"
    >
      <div className="relative max-w-3xl mx-auto px-5 sm:px-6 lg:px-10">
        {/* Heading */}
        <div className="text-center">
          <Reveal>
            <p className="text-[12px] font-bold uppercase tracking-[0.22em] text-brand-gold">
              Delivery Fee Calculator
            </p>
          </Reveal>
          <Reveal delay={1}>
            <h2 className="mt-2.5 font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white leading-tight">
              Know Your Delivery Cost
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-3 text-[13.5px] sm:text-[14.5px] text-white/70 leading-relaxed max-w-xl mx-auto">
              Drop in your ZIP — NYC&rsquo;s 5 boroughs plus Nassau and
              Western Suffolk. Fee includes professional transport, hot/cold
              packaging, and basic setup.
            </p>
          </Reveal>
        </div>

        {/* Calculator card */}
        <Reveal delay={2}>
          <div
            className="relative mt-6 sm:mt-8 rounded-2xl sm:rounded-3xl bg-white border border-black/5 shadow-[0_30px_70px_-30px_rgba(0,0,0,0.7)] p-5 sm:p-7 overflow-hidden"
            data-testid="delivery-calculator-card"
          >
            <div
              aria-hidden="true"
              className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-brand-teal via-brand-teal/70 to-brand-gold"
            />

            <div className="flex items-start gap-3">
              <span className="inline-flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-charcoal text-brand-teal">
                <Calculator className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
              </span>
              <div>
                <h3 className="font-display text-[17px] sm:text-[19px] font-bold text-brand-charcoal tracking-tight">
                  Enter your ZIP code
                </h3>
                <p className="mt-0.5 text-[12.5px] sm:text-[13px] text-brand-charcoal/65 leading-snug">
                  We&rsquo;ll match it to the right delivery tier in real time.
                </p>
              </div>
            </div>

            <div className="mt-5">
              <label
                htmlFor="zip-input"
                className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-charcoal/60"
              >
                ZIP Code
              </label>
              <div className="mt-1.5 relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-charcoal/40 pointer-events-none" />
                <Input
                  id="zip-input"
                  data-testid="delivery-zip-input"
                  inputMode="numeric"
                  pattern="\d{5}"
                  maxLength={5}
                  placeholder="e.g. 11432"
                  value={zip}
                  onChange={onChange}
                  onBlur={() => setTouched(true)}
                  className="h-12 sm:h-13 pl-11 pr-4 rounded-2xl text-[17px] sm:text-[18px] tracking-[0.18em] font-bold bg-brand-cream/40 border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                  aria-describedby="zip-help"
                />
              </div>
              {showInvalid ? (
                <p
                  id="zip-help"
                  data-testid="delivery-zip-error"
                  className="mt-2 text-[12px] font-medium text-red-600"
                >
                  Please enter a valid 5-digit ZIP code.
                </p>
              ) : null}
            </div>

            {/* Result */}
            <div className="mt-5">
              {!fiveDigits ? (
                <div
                  data-testid="delivery-result-empty"
                  className="rounded-2xl border border-dashed border-black/10 bg-brand-gray/50 px-5 py-5 text-center"
                >
                  <Truck className="h-5 w-5 text-brand-charcoal/40 mx-auto" />
                  <p className="mt-1.5 text-[12.5px] text-brand-charcoal/60">
                    Enter your ZIP to see the flat delivery fee for your area.
                  </p>
                </div>
              ) : result && result.tier !== "unknown" ? (
                <div
                  data-testid={`delivery-result-${result.tier}`}
                  className="rounded-2xl bg-brand-charcoal text-white px-5 sm:px-6 py-5 relative overflow-hidden"
                >
                  <div
                    aria-hidden="true"
                    className="absolute -top-12 -right-10 h-40 w-40 rounded-full bg-brand-teal/25 blur-3xl"
                  />
                  <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-gold">
                        {result.title}
                      </p>
                      <p className="mt-1 font-display font-bold text-[16px] sm:text-[17px] text-white tracking-tight">
                        ZIP {zip} · {result.area}
                      </p>
                      <p className="mt-1.5 text-[12px] text-white/70 leading-snug max-w-md">
                        Includes professional transport and basic setup. Hot or
                        cold packaging, labeled trays, ready to serve.
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">
                        Flat Fee
                      </p>
                      <p
                        data-testid="delivery-result-fee"
                        className="font-display font-black text-[38px] sm:text-[44px] leading-none text-brand-gold tabular-nums"
                      >
                        ${result.fee}
                      </p>
                    </div>
                  </div>
                  <div className="relative mt-4 flex flex-wrap items-center gap-3">
                    <Link
                      to="/menu"
                      data-testid="delivery-go-shop-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-charcoal px-4 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:bg-white transition-colors"
                    >
                      <ShoppingBag className="h-3.5 w-3.5" />
                      Apply Fee &amp; Shop Menu
                    </Link>
                    <p className="text-[11px] text-white/55">
                      Fee saved to your cart for ZIP {zip}.
                    </p>
                  </div>
                </div>
              ) : (
                <div
                  data-testid="delivery-result-unknown"
                  className="rounded-2xl bg-brand-cream border border-brand-gold/30 px-5 py-5"
                >
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-teal">
                    Custom Quote
                  </p>
                  <p className="mt-1.5 font-display font-bold text-[16px] sm:text-[17px] text-brand-charcoal tracking-tight">
                    ZIP {zip} is outside our standard delivery zones.
                  </p>
                  <p className="mt-1.5 text-[12.5px] text-brand-charcoal/70 leading-snug max-w-md">
                    Call us for a custom delivery quote — we serve specialty
                    events beyond standard zones with advance notice.
                  </p>
                  <a
                    href={`tel:${BRAND.phoneTel}`}
                    data-testid="delivery-call-cta"
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    Call {BRAND.phone}
                  </a>
                </div>
              )}
            </div>

            <p className="mt-4 text-[11px] text-brand-charcoal/50 leading-snug">
              Flat fees per delivery; same-day, after-hours, and staffed events
              may carry additional fees — quoted on request.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default DeliveryCalculator;
